import { a as isPotentialPHIField, c as sanitizeMetadata, i as hashUserId, l as validateEventName, n as SAFE_NAME_TERMS, o as isPotentialPHIValue, r as createSafeMetadata, s as sanitizeErrorMessage, t as DEFAULT_PHI_FIELDS } from "./sanitizer-CLnKD_rP.js";
import { n as StatsigSettingsContext, r as useStatsigSettings, t as useTrackEvent } from "./useTrackEvent-IE9Pu1uh.js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { StatsigProvider as StatsigProvider$1, useDynamicConfig, useExperiment, useLayer, useStatsigClient, useStatsigClient as useStatsigClient$1, useStatsigUser, useStatsigUser as useStatsigUser$1 } from "@statsig/react-bindings";
import { Fragment, jsx } from "react/jsx-runtime";

//#region src/localOverrides.ts
/**
* Construct the adapter with the overrides persisted by earlier page loads
* already in memory. The client also calls `loadFromStorage()` on the adapter
* it is constructed with, but asynchronously — waiting here is what lets the
* first evaluation after a reload see the override.
*/
async function loadLocalOverrideAdapter(load, sdkKey) {
	try {
		const { LocalOverrideAdapter } = await load();
		const adapter = new LocalOverrideAdapter(sdkKey);
		await adapter.loadFromStorage();
		return adapter;
	} catch (error) {
		console.warn("[Statsig] Local overrides could not be loaded; gates evaluate without overrides.", error);
		return;
	}
}
/** Expose the adapter and a debug helper on `window` for browser-console use. */
function exposeLocalOverrides(adapter, client) {
	const target = window;
	target.statsigOverrides = adapter;
	target.statsigDebug = {
		showOverrides: () => adapter.getAllOverrides(),
		showStorage: () => Object.fromEntries(Object.keys(localStorage).filter((key) => key.includes("local-overrides")).map((key) => [key, localStorage.getItem(key)])),
		checkGate: (name) => {
			const gate = client.getFeatureGate(name, { disableExposureLog: true });
			return {
				value: gate.value,
				reason: gate.details.reason
			};
		}
	};
}
/**
* The adapter the client is constructed with. The SDK accepts an override
* adapter only at construction but consults it on every evaluation, so
* delegating to an adapter attached later lets overrides loaded after the
* client exists apply to every evaluation made from then on.
*/
var DeferredOverrideAdapter = class {
	constructor() {
		this.inner = null;
	}
	attach(adapter) {
		this.inner = adapter;
	}
	loadFromStorage() {
		return this.inner?.loadFromStorage?.();
	}
	getGateOverride(...args) {
		return this.inner?.getGateOverride?.(...args) ?? null;
	}
	getDynamicConfigOverride(...args) {
		return this.inner?.getDynamicConfigOverride?.(...args) ?? null;
	}
	getExperimentOverride(...args) {
		return this.inner?.getExperimentOverride?.(...args) ?? null;
	}
	getLayerOverride(...args) {
		return this.inner?.getLayerOverride?.(...args) ?? null;
	}
	getParamStoreOverride(...args) {
		return this.inner?.getParamStoreOverride?.(...args) ?? null;
	}
};

//#endregion
//#region src/components/StatsigProvider.tsx
const ANONYMOUS_USER$1 = {};
/** How long the provider waits for a loader given on mount before rendering without it. */
const LOCAL_OVERRIDES_LOAD_TIMEOUT_MS = 3e3;
/**
* Inner component that exposes the initialized client and propagates debug settings
* to the settings context that hooks read from.
*/
const StatsigClientBridge = ({ debugLogEvents, overrideAdapter, onClientReady, children }) => {
	const { client } = useStatsigClient$1();
	useEffect(() => {
		if (overrideAdapter) exposeLocalOverrides(overrideAdapter, client);
	}, [client, overrideAdapter]);
	useEffect(() => {
		if (onClientReady) onClientReady(client);
	}, [client, onClientReady]);
	const settings = useMemo(() => ({ debugLogEvents }), [debugLogEvents]);
	return /* @__PURE__ */ jsx(StatsigSettingsContext.Provider, {
		value: settings,
		children
	});
};
/**
* Load the override adapter and attach it to the deferred one the client is
* constructed with. Loads once, for the first loader supplied, whenever that
* is; only a loader present on mount is waited for.
*/
function useLocalOverrides(sdkKey, load, timeoutMs) {
	const [deferred] = useState(() => new DeferredOverrideAdapter());
	const [waiting, setWaiting] = useState(() => Boolean(load && sdkKey));
	const [adapter, setAdapter] = useState(void 0);
	const startedRef = useRef(false);
	const mountedRef = useRef(true);
	useEffect(() => () => {
		mountedRef.current = false;
	}, []);
	useEffect(() => {
		if (startedRef.current || !load || !sdkKey) return;
		startedRef.current = true;
		const attach = async () => {
			const loaded = await loadLocalOverrideAdapter(load, sdkKey);
			if (!mountedRef.current) return;
			if (loaded) {
				deferred.attach(loaded);
				setAdapter(loaded);
			}
			setWaiting(false);
		};
		attach();
	}, [
		deferred,
		load,
		sdkKey
	]);
	useEffect(() => {
		if (!waiting) return;
		const timer = setTimeout(() => {
			console.warn(`[Statsig] Local overrides did not load within ${String(timeoutMs)}ms; rendering without them. They attach when the load completes; reload to apply persisted overrides to every evaluation.`);
			setWaiting(false);
		}, timeoutMs);
		return () => clearTimeout(timer);
	}, [waiting, timeoutMs]);
	return {
		deferred,
		waiting,
		adapter
	};
}
/**
* Statsig provider wrapper.
*
* Plugins (auto-capture, session replay) must be constructed by the consumer
* and passed via the `plugins` prop — Statsig binds them only at client
* construction on first render. Use the helpers in `@jotpsych/statsig/plugins`
* to get pre-wired PHI-safe instances.
*
* @example
* import { StatsigProvider } from '@jotpsych/statsig';
* import {
*   createSanitizedAutoCapturePlugin,
*   createTriggeredSessionReplayPlugin,
* } from '@jotpsych/statsig/plugins';
*
* const plugins = useMemo(
*   () => [
*     createSanitizedAutoCapturePlugin(),
*     createTriggeredSessionReplayPlugin(),
*   ],
*   [],
* );
*
* <StatsigProvider
*   sdkKey={import.meta.env.VITE_STATSIG_SDK_KEY}
*   user={{ custom: { surface: 'web-app' } }}
*   environment={import.meta.env.MODE}
*   debugLogEvents={import.meta.env.DEV}
*   plugins={plugins}
*   loadLocalOverrides={
*     import.meta.env.DEV ? () => import('@statsig/js-local-overrides') : undefined
*   }
*   onClientReady={(client) => bridgeClient(client)}
* >
*   <App />
* </StatsigProvider>
*/
const StatsigProvider = ({ sdkKey, user, environment = "production", debugLogEvents = false, plugins, loadLocalOverrides, loadLocalOverridesTimeoutMs = LOCAL_OVERRIDES_LOAD_TIMEOUT_MS, onClientReady, children }) => {
	const { deferred, waiting, adapter } = useLocalOverrides(sdkKey, loadLocalOverrides, loadLocalOverridesTimeoutMs);
	const options = useMemo(() => ({
		environment: { tier: environment },
		overrideAdapter: deferred,
		plugins
	}), [
		plugins,
		deferred,
		environment
	]);
	if (!sdkKey) {
		console.warn("[Statsig] No SDK key provided; rendering children without Statsig context.");
		return /* @__PURE__ */ jsx(Fragment, { children });
	}
	if (waiting) return null;
	return /* @__PURE__ */ jsx(StatsigProvider$1, {
		sdkKey,
		user: user ?? ANONYMOUS_USER$1,
		options,
		children: /* @__PURE__ */ jsx(StatsigClientBridge, {
			debugLogEvents,
			overrideAdapter: adapter,
			onClientReady,
			children
		})
	});
};

//#endregion
//#region src/hooks/useFeatureGate.ts
/**
* Check a single Statsig feature gate.
*
* The result is memoized to avoid duplicate exposure logs on re-renders.
* Statsig automatically logs an exposure event the first time a gate is checked.
*
* @example
* const showNewUi = useFeatureGate('feature_new_ui');
* return showNewUi ? <NewUi /> : <OldUi />;
*/
function useFeatureGate(gateName) {
	const { checkGate } = useStatsigClient$1();
	return useMemo(() => {
		return checkGate(gateName);
	}, [checkGate, gateName]);
}
/**
* Check multiple Statsig feature gates at once. Each gate logs its own exposure.
*
* @example
* const gates = useFeatureGates(['feature_a', 'feature_b']);
* if (gates.feature_a && gates.feature_b) { ... }
*/
function useFeatureGates(gateNames) {
	const { checkGate } = useStatsigClient$1();
	const gateNamesKey = useMemo(() => JSON.stringify(gateNames), [gateNames]);
	return useMemo(() => {
		return JSON.parse(gateNamesKey).reduce((acc, gateName) => {
			acc[gateName] = checkGate(gateName);
			return acc;
		}, {});
	}, [checkGate, gateNamesKey]);
}

//#endregion
//#region src/hooks/useStatsigUserSync.ts
const ANONYMOUS_USER = {};
const INITIAL_STATE = {
	settledKey: null,
	error: null,
	ready: false
};
/**
* Identity most recently pushed through each client, recorded when the update
* is issued, and the identity whose update last settled without being
* superseded. Several hook instances share one client (an authenticated shell
* and a public page, for example), so whether the client still holds the
* provider's anonymous identity is a fact about the client, not about any one
* instance. A client with no entry has only ever held the provider's user.
*/
const requestedIdentity = /* @__PURE__ */ new WeakMap();
const settledIdentity = /* @__PURE__ */ new WeakMap();
/**
* Sync a consumer-owned Statsig user into the client and expose two readiness
* signals: a sticky `isReady` for gating first render, and an identity-accurate
* `isSynced` for consumers that must never act on a stale identity.
*
* Must be called inside a `<StatsigProvider>` from `@jotpsych/statsig`.
*
* The hook owns the parts that are easy to get subtly wrong:
* - **Content-keyed memoization** — re-syncs only when the user's content
*   actually changes, not on every reference churn from the consumer's user
*   store.
* - **Sticky ready gate** — holds `isReady: false` until the first sync
*   settles, then keeps it `true` for the life of the hook. Attribute changes
*   re-sync in the background.
* - **Identity-accurate `isSynced`** — an obsolete completion never marks a
*   newer identity as synced.
* - **Fail-open on error** — sync failures still flip `isReady: true` so a
*   Statsig outage doesn't block the whole app.
* - **Anonymous fast-path and restore** — `isAuthenticated: false` marks ready
*   instantly. The anonymous identity is pushed back to the client in the
*   background whenever the client is not known to hold it already, even when
*   a different hook instance was the one that signed in.
*
* @example
* const user = useMyAppStatsigUser();
* const { isReady } = useStatsigUserSync({ user, isAuthenticated });
* return isReady ? <App /> : <Spinner />;
*/
function useStatsigUserSync({ user, isAuthenticated, anonymousUser = ANONYMOUS_USER }) {
	const { client } = useStatsigClient$1();
	const { updateUserAsync } = useStatsigUser$1();
	const desiredUser = isAuthenticated ? user : anonymousUser;
	const desiredKey = desiredUser ? JSON.stringify(desiredUser) : null;
	const stableUser = useMemo(() => desiredKey === null ? null : JSON.parse(desiredKey), [desiredKey]);
	const [state, setState] = useState(INITIAL_STATE);
	useEffect(() => {
		if (!stableUser || desiredKey === null) return;
		if (!isAuthenticated) {
			const requested = requestedIdentity.get(client);
			if (requested === void 0 || requested === desiredKey && settledIdentity.get(client) === desiredKey) {
				setState((prev) => prev.ready && prev.error === null && prev.settledKey === desiredKey ? prev : {
					settledKey: desiredKey,
					error: null,
					ready: true
				});
				return;
			}
		}
		let cancelled = false;
		requestedIdentity.set(client, desiredKey);
		const sync = async () => {
			let error = null;
			try {
				const result = await updateUserAsync(stableUser);
				if (result && !result.success) error = /* @__PURE__ */ new Error("Statsig identity evaluation failed");
			} catch (cause) {
				error = cause instanceof Error ? cause : new Error(String(cause));
			}
			if (requestedIdentity.get(client) === desiredKey) settledIdentity.set(client, desiredKey);
			if (cancelled) return;
			setState({
				settledKey: desiredKey,
				error,
				ready: true
			});
		};
		sync();
		return () => {
			cancelled = true;
		};
	}, [
		client,
		stableUser,
		desiredKey,
		isAuthenticated,
		updateUserAsync
	]);
	const isSynced = desiredKey !== null && state.settledKey === desiredKey;
	return {
		isReady: state.ready || !isAuthenticated,
		isSynced,
		error: isSynced ? state.error : null
	};
}

//#endregion
export { DEFAULT_PHI_FIELDS, SAFE_NAME_TERMS, StatsigProvider, StatsigSettingsContext, createSafeMetadata, hashUserId, isPotentialPHIField, isPotentialPHIValue, sanitizeErrorMessage, sanitizeMetadata, useDynamicConfig, useExperiment, useFeatureGate, useFeatureGates, useLayer, useStatsigClient, useStatsigSettings, useStatsigUser, useStatsigUserSync, useTrackEvent, validateEventName };
//# sourceMappingURL=index.js.map